import { useCallback } from 'react';
import { useEvent, useUnit } from 'effector-react';
import { ButtonSize } from '@nx-frontend/shared/ui/button';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useNavigateMenuItem } from '@nx-frontend/shared/hooks';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { getMenuItemByIdFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $path } from '@nx-frontend/shared/store/portal-app';
import { ChangeEventType, isFolderMenuItemType, MenuType, PopupType } from '@nx-frontend/shared/types';

export function useOpenDeletePortalEntityDialog() {
  const [path] = useUnit([$path]);
  const [openModal, closeModal, getMenuItemById] = useEvent([openModalEvent, closeModalEvent, getMenuItemByIdFx]);
  const setChanges = useChanges();
  const navigateMenuItem = useNavigateMenuItem();

  return useCallback(
    async ({ id, menuType, onClose }: { id: string; menuType: MenuType; onClose?: () => void }) => {
      const item = await getMenuItemById({ id: id ?? '', menuType });
      const isFolder = isFolderMenuItemType(item);

      const deleteAction = () => {
        setChanges({
          type: ChangeEventType.DELETE_MENU_ITEM,
          oldValue: { menuType, item: item },
          newValue: undefined,
        });
        closeModal();
        onClose?.();
        if (path === item.url || path.includes(item.url + '/')) {
          navigateMenuItem({ path: '/' });
        }
      };

      if (!item.isOrigin) {
        openModal({
          title: 'Deletion confirmation',
          content: PopupType.DIALOG,
          showCloseButton: true,
          closeButtonSize: 18,
          onClose,
          payload: {
            payloadType: PopupType.DIALOG,
            text: 'Are you sure you want to delete "' + item.name + '"?',
            buttons: [
              {
                text: 'Cancel',
                size: ButtonSize.XL,
              },
              {
                text: 'Delete',
                main: true,
                size: ButtonSize.XL,
                onClick: async () => {
                  deleteAction();
                },
              },
            ],
          },
        });
      } else {
        openModal({
          title: `You are going to delete original ${isFolder ? 'folder' : 'page'}`,
          content: PopupType.DELETE_PROJECTION_ORIGIN,
          showCloseButton: true,
          closeButtonSize: 18,
          onClose,
          payload: {
            payloadType: PopupType.DELETE_PROJECTION_ORIGIN,
            menuItemId: item.id,
            menuItemType: item.type,
            submit: (removeOrigin: boolean) => {
              if (removeOrigin) {
                setChanges({
                  type: ChangeEventType.UPDATE_MENU_ITEM,
                  oldValue: { menuType, item },
                  newValue: { menuType, item: { ...item, isOrigin: null } },
                });
              }
              deleteAction();
            },
          },
        });
      }
    },
    [closeModal, getMenuItemById, navigateMenuItem, openModal, path, setChanges]
  );
}
